"use client"

import type { UseCaseCategory, useCases } from "@/app/(marketing)/use-cases/_data/use-cases"
import type { Language } from "@/app/(marketing)/use-cases/_lib/i18n"
import { useEffect, useRef, useState } from "react"
import { UseCaseCard } from "./use-case-card"

const INITIAL_COUNT = 15
const LOAD_MORE_COUNT = 15

export function CardGrid({
  cases,
  selectedCategory,
  language,
  onCardClick,
}: {
  cases: (typeof useCases)[number][]
  selectedCategory: UseCaseCategory | null
  language: Language
  onCardClick: (e: React.MouseEvent, href: string) => void
}) {
  // ── ページング ──
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT)
  const prevVisibleCount = useRef(INITIAL_COUNT)

  useEffect(() => {
    setVisibleCount(INITIAL_COUNT)
    prevVisibleCount.current = INITIAL_COUNT
  }, [selectedCategory])

  const visibleCases = cases.slice(0, visibleCount)
  const hasMore = visibleCount < cases.length

  // ── カテゴリ切替フェード ──
  const [fading, setFading] = useState(false)
  const [displayed, setDisplayed] = useState(visibleCases)
  const [ready, setReady] = useState(false)

  // ページfadeInUp完了後にカードtransitionを有効化
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 700)
    return () => clearTimeout(timer)
  }, [])

  // カテゴリ切り替え
  useEffect(() => {
    if (!ready) {
      setDisplayed(visibleCases)
      return
    }
    setFading(true)
    const timer = setTimeout(() => {
      setDisplayed(visibleCases)
      setFading(false)
      prevVisibleCount.current = visibleCount
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory])

  // もっと見る
  const prevCount = useRef(visibleCount)
  useEffect(() => {
    if (!ready) return
    if (visibleCount !== prevCount.current && visibleCount !== INITIAL_COUNT) {
      setDisplayed(visibleCases)
      const timer = setTimeout(() => {
        prevVisibleCount.current = visibleCount
      }, 500)
      prevCount.current = visibleCount
      return () => clearTimeout(timer)
    }
    prevCount.current = visibleCount
  }, [visibleCount, visibleCases, ready])

  // ready=false → クラスなし（ページfadeInUpに任せる）
  const fadeClass = !ready
    ? ""
    : fading
      ? "transition-all duration-500 ease-in-out opacity-0 translate-y-2"
      : "transition-all duration-500 ease-in-out opacity-100 translate-y-0"

  return (
    <>
      <div className={`lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-5 ${fadeClass}`}>
        {displayed.map((uc, i) => (
          <UseCaseCard
            key={uc.slug}
            uc={uc}
            language={language}
            onCardClick={onCardClick}
            animate={i >= prevVisibleCount.current}
          />
        ))}
      </div>

      <div className={`hidden lg:grid lg:grid-cols-3 flex-1 gap-5 auto-rows-min ${fadeClass}`}>
        {displayed.map((uc, i) => (
          <UseCaseCard
            key={uc.slug}
            uc={uc}
            language={language}
            onCardClick={onCardClick}
            animate={i >= prevVisibleCount.current}
          />
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-10">
          <button
            onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_COUNT)}
            className="px-6 py-3 rounded-full border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors"
            style={{ color: "#26251f" }}
          >
            もっと見る
          </button>
        </div>
      )}
    </>
  )
}
